import React, { useCallback, useState } from 'react';
import { Droplet, Save, RefreshCw, Download, Calculator, Activity, ThermometerSun, Sun, HeartPulse } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import PersonalParametersSection from './PersonalParametersSection';
import ActivityAndEnvironmentSection from './ActivityAndEnvironmentSection';
import HealthConditionsSection from './HealthConditionsSection';
import IntakePreferencesSection from './IntakePreferencesSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета суточной нормы воды
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  setSchedule,
  generateSchedule,
  activityLevels,
  climateTypes,
  seasons,
  healthConditionsList,
  saveProfile 
}) => {
  
  // Состояние для имени сохраняемого профиля
  const [saveProfileName, setSaveProfileName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      // Для чекбоксов состояний здоровья
      if (name.startsWith('health_')) {
        const conditionId = name.replace('health_', '');
        const updatedConditions = [...prev.healthConditions];
        
        if (checked) {
          if (!updatedConditions.includes(conditionId)) {
            updatedConditions.push(conditionId);
          }
        } else {
          const index = updatedConditions.indexOf(conditionId);
          if (index > -1) {
            updatedConditions.splice(index, 1);
          }
        }
        
        return { ...prev, healthConditions: updatedConditions };
      }
      
      // Для полей в подобъекте waterIntakePreferences
      if (name.includes('.')) {
        const [parentField, childField] = name.split('.');
        return {
          ...prev,
          [parentField]: {
            ...prev[parentField],
            [childField]: fieldValue
          }
        };
      }
      
      // Для обычных полей
      return { ...prev, [name]: fieldValue };
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Функция для парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      weight, height, age, gender, activityLevel, climate, season,
      healthConditions, pregnancyLactation, waterIntakePreferences
    } = formData;

    // Проверка личных параметров
    if (!isPositiveNumber(weight) || parseFloat(weight) <= 0) {
      newErrors.weight = 'Вес должен быть положительным числом.';
    } else if (parseFloat(weight) > 300) {
      newErrors.weight = 'Вес не может быть больше 300 кг.';
    }

    if (!isPositiveNumber(height) || parseFloat(height) <= 0) {
      newErrors.height = 'Рост должен быть положительным числом.';
    } else if (parseFloat(height) > 250) {
      newErrors.height = 'Рост не может быть больше 250 см.';
    }

    if (!isInteger(age) || parseInt(age) <= 0) {
      newErrors.age = 'Возраст должен быть целым положительным числом.';
    } else if (parseInt(age) > 120) {
      newErrors.age = 'Возраст не может быть больше 120 лет.';
    }

    if (!gender || !['male', 'female'].includes(gender)) {
      newErrors.gender = 'Пожалуйста, выберите пол.';
    }

    // Проверка активности и окружения
    if (!activityLevel) {
      newErrors.activityLevel = 'Пожалуйста, выберите уровень активности.';
    }

    if (!climate) {
      newErrors.climate = 'Пожалуйста, выберите тип климата.';
    }

    if (!season) {
      newErrors.season = 'Пожалуйста, выберите сезон.';
    }

    // Проверка настроек потребления
    if (waterIntakePreferences) {
      const { preferredContainerSize, wakeUpTime, bedTime } = waterIntakePreferences;
      
      if (!isPositiveNumber(preferredContainerSize) || parseInt(preferredContainerSize) <= 0) {
        newErrors['waterIntakePreferences.preferredContainerSize'] = 'Укажите корректный объем емкости.';
      } else if (parseInt(preferredContainerSize) > 2000) {
        newErrors['waterIntakePreferences.preferredContainerSize'] = 'Объем не может быть больше 2000 мл.';
      }

      if (!wakeUpTime) {
        newErrors['waterIntakePreferences.wakeUpTime'] = 'Укажите время пробуждения.';
      }

      if (!bedTime) {
        newErrors['waterIntakePreferences.bedTime'] = 'Укажите время отхода ко сну.';
      }
    }

    // Специальные проверки для женщин
    if (gender === 'female' && !['none', 'pregnant', 'lactating'].includes(pregnancyLactation)) {
      newErrors.pregnancyLactation = 'Пожалуйста, выберите состояние.';
    }

    // Проверка состояний здоровья
    if (healthConditions.length > 0) {
      const medicalConditions = healthConditions.filter(condition => {
        const healthCondition = healthConditionsList.find(hc => hc.id === condition);
        return healthCondition && healthCondition.factor === 0.0;
      });

      if (medicalConditions.length > 0) {
        newErrors.healthConditions = 'Выбранные состояния здоровья требуют консультации с врачом. Расчет будет приблизительным.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, healthConditionsList, setErrors]);

  // Функция расчета суточной нормы потребления воды
  const calculateWaterIntake = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setSchedule([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const weight = parseFloatOrDefault(formData.weight);
        const height = parseFloatOrDefault(formData.height);
        const age = parseIntOrDefault(formData.age);
        const gender = formData.gender;
        const activityLevel = formData.activityLevel;
        const climate = formData.climate;
        const season = formData.season;
        const healthConditions = formData.healthConditions;
        const pregnancyLactation = formData.pregnancyLactation;
        const dietaryPreferences = formData.dietaryPreferences;
        const caffeineConsumption = formData.caffeineConsumption;
        const alcoholConsumption = formData.alcoholConsumption;
        const includesFoodWater = formData.waterIntakePreferences.includesFoodWater;
        const includesOtherBeverages = formData.waterIntakePreferences.includesOtherBeverages;

        // Получаем коэффициенты из базовых таблиц
        const activityFactor = activityLevels.find(al => al.id === activityLevel)?.factor || 1.0;
        const climateFactor = climateTypes.find(c => c.id === climate)?.factor || 1.0;
        const seasonFactor = seasons.find(s => s.id === season)?.factor || 1.0;

        // Рассчитываем базовую норму воды по рекомендациям ВОЗ и НИИ питания РАМН
        // Базовая формула: 30-40 мл на 1 кг веса
        let baseWaterIntakePerKg;
        
        if (age < 18) {
          // Для детей и подростков
          baseWaterIntakePerKg = 40;
        } else if (age > 60) {
          // Для пожилых людей
          baseWaterIntakePerKg = 30;
        } else {
          // Для взрослых
          baseWaterIntakePerKg = gender === 'male' ? 35 : 31;
        }

        // Рассчитываем базовую норму воды
        let baseWaterIntake = weight * baseWaterIntakePerKg;

        // Применяем корректировку по возрасту
        if (age > 60) {
          // Для пожилых людей важно пить достаточно воды, но не переусердствовать
          baseWaterIntake *= 0.95;
        }

        // Применяем коэффициенты активности, климата и сезона
        let adjustedWaterIntake = baseWaterIntake * activityFactor * climateFactor * seasonFactor;

        // Учитываем состояния здоровья
        let healthFactor = 1.0;
        let healthWarnings = [];

        if (healthConditions.length > 0) {
          healthConditions.forEach(conditionId => {
            const condition = healthConditionsList.find(hc => hc.id === conditionId);
            if (condition) {
              if (condition.factor === 0.0) {
                // Состояния, требующие консультации с врачом
                healthWarnings.push(condition.name);
              } else {
                // Состояния с определенным коэффициентом
                healthFactor *= condition.factor;
              }
            }
          });
        }

        adjustedWaterIntake *= healthFactor;

        // Учитываем беременность и лактацию
        if (gender === 'female') {
          if (pregnancyLactation === 'pregnant') {
            // Дополнительно 300 мл в день для беременных
            adjustedWaterIntake += 300;
          } else if (pregnancyLactation === 'lactating') {
            // Дополнительно 700-800 мл в день для кормящих
            adjustedWaterIntake += 750;
          }
        }

        // Учитываем диетические предпочтения
        if (dietaryPreferences === 'high_protein') {
          // Высокобелковая диета требует больше воды для метаболизма белка
          adjustedWaterIntake *= 1.1;
        } else if (dietaryPreferences === 'vegetarian' || dietaryPreferences === 'vegan') {
          // Растительная пища содержит больше воды
          adjustedWaterIntake *= 0.95;
        }

        // Учитываем потребление кофеина и алкоголя
        if (caffeineConsumption === 'high') {
          // Высокое потребление кофеина приводит к дегидратации
          adjustedWaterIntake *= 1.1;
        }

        if (alcoholConsumption === 'moderate' || alcoholConsumption === 'high') {
          // Алкоголь приводит к дегидратации
          adjustedWaterIntake *= alcoholConsumption === 'high' ? 1.15 : 1.08;
        }

        // Учитываем включение воды из пищи (около 20% суточной потребности)
        let pureWaterIntake = adjustedWaterIntake;
        let waterFromFood = 0;
        let waterFromOtherBeverages = 0;

        if (includesFoodWater) {
          // Вода из пищи составляет около 20% от общей потребности
          waterFromFood = adjustedWaterIntake * 0.2;
          pureWaterIntake -= waterFromFood;
        }

        // Учитываем другие напитки (чай, соки и т.д., около 30% от потребности)
        if (includesOtherBeverages) {
          waterFromOtherBeverages = adjustedWaterIntake * 0.3;
          pureWaterIntake -= waterFromOtherBeverages;
        }

        // Округляем результаты до целых чисел
        pureWaterIntake = Math.round(pureWaterIntake);
        waterFromFood = Math.round(waterFromFood);
        waterFromOtherBeverages = Math.round(waterFromOtherBeverages);
        adjustedWaterIntake = Math.round(adjustedWaterIntake);

        // Подсчет количества стаканов
        const containerSize = parseInt(formData.waterIntakePreferences.preferredContainerSize) || 250;
        const numberOfContainers = Math.ceil(pureWaterIntake / containerSize);

        // Дополнительные рекомендации
        let recommendations = [];

        // Рекомендации на основе уровня активности
        if (activityLevel === 'active' || activityLevel === 'very_active') {
          recommendations.push("При интенсивных тренировках пейте дополнительно 200-300 мл воды за каждые 20-30 минут физической активности.");
        }

        // Рекомендации на основе климата
        if (climate === 'hot') {
          recommendations.push("В жаркую погоду увеличьте потребление воды на 500-1000 мл в день и пейте регулярно, даже если не испытываете жажду.");
        }

        // Рекомендации по здоровью
        if (healthWarnings.length > 0) {
          recommendations.push(`При наличии таких заболеваний, как ${healthWarnings.join(", ")}, проконсультируйтесь с врачом о режиме питья.`);
        }

        // Общие рекомендации
        recommendations.push("Распределите потребление воды равномерно в течение дня, начиная со стакана воды сразу после пробуждения.");
        recommendations.push("Старайтесь пить воду за 30 минут до еды и через 1,5-2 часа после приема пищи.");
        recommendations.push("Ограничьте потребление воды за 2 часа до сна, чтобы избежать ночных пробуждений.");

        // Финальные результаты
        const finalResults = {
          recommendedTotalIntakeMl: adjustedWaterIntake,
          recommendedWaterIntakeMl: pureWaterIntake,
          waterFromFoodMl: waterFromFood,
          waterFromOtherBeveragesMl: waterFromOtherBeverages,
          numberOfContainers: numberOfContainers,
          containerSizeMl: containerSize,
          baseFactors: {
            weight: weight,
            height: height,
            age: age,
            gender: gender,
            baseWaterPerKg: baseWaterIntakePerKg
          },
          adjustmentFactors: {
            activity: activityFactor,
            climate: climateFactor,
            season: seasonFactor,
            health: healthFactor
          },
          healthWarnings: healthWarnings,
          recommendations: recommendations
        };

        setResults(finalResults);
        
        // Генерируем график потребления
        generateSchedule(finalResults, formData);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setSchedule([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setSchedule, setIsCalculating, generateSchedule, activityLevels, climateTypes, seasons, healthConditionsList]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      weight: '70',
      height: '170',
      age: '30',
      gender: 'male',
      activityLevel: 'moderate',
      climate: 'temperate',
      season: getCurrentSeason(),
      healthConditions: [],
      pregnancyLactation: 'none',
      dietaryPreferences: 'standard',
      caffeineConsumption: 'moderate',
      alcoholConsumption: 'none',
      waterIntakePreferences: {
        includesFoodWater: true,
        includesOtherBeverages: true,
        preferredContainerSize: '250',
        wakeUpTime: '07:00',
        bedTime: '23:00',
      }
    });
    setResults(null);
    setSchedule([]);
    setErrors({});
    setSaveProfileName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов и рекомендаций в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    const headers = ['Параметр', 'Значение'];
    
    const rows = [
      ['Расчет суточной нормы потребления воды', ''],
      ['Дата расчета', new Date().toLocaleString('ru-RU')],
      [''],
      ['ПЕРСОНАЛЬНЫЕ ДАННЫЕ', ''],
      ['Вес', `${formData.weight} кг`],
      ['Рост', `${formData.height} см`],
      ['Возраст', `${formData.age} лет`],
      ['Пол', formData.gender === 'male' ? 'Мужской' : 'Женский'],
      ['Уровень активности', activityLevels.find(al => al.id === formData.activityLevel)?.name || ''],
      ['Климат', climateTypes.find(c => c.id === formData.climate)?.name || ''],
      ['Сезон', seasons.find(s => s.id === formData.season)?.name || ''],
      [''],
      ['РЕЗУЛЬТАТЫ РАСЧЕТА', ''],
      ['Общая рекомендуемая норма жидкости', `${results.recommendedTotalIntakeMl} мл/день`],
      ['Чистой воды', `${results.recommendedWaterIntakeMl} мл/день`],
      ['Вода из пищи', `${results.waterFromFoodMl} мл/день`],
      ['Другие напитки', `${results.waterFromOtherBeveragesMl} мл/день`],
      ['Количество емкостей', `${results.numberOfContainers} × ${results.containerSizeMl} мл`],
      [''],
      ['РЕКОМЕНДАЦИИ', ''],
    ];

    // Добавляем рекомендации
    results.recommendations.forEach((rec, index) => {
      rows.push([`Рекомендация ${index + 1}`, rec]);
    });

    // Если есть предупреждения о здоровье
    if (results.healthWarnings.length > 0) {
      rows.push(['']);
      rows.push(['ВАЖНО! СОСТОЯНИЯ ЗДОРОВЬЯ, ТРЕБУЮЩИЕ КОНСУЛЬТАЦИИ С ВРАЧОМ', '']);
      results.healthWarnings.forEach((warning, index) => {
        rows.push([`Состояние ${index + 1}`, warning]);
      });
    }

    // Форматируем данные для CSV
    const csvContent = '\uFEFF' + rows.map(row => row.join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_нормы_воды.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Определение текущего сезона для начальных значений
  function getCurrentSeason() {
    const month = new Date().getMonth();
    if (month >= 11 || month <= 1) return 'winter';
    if (month >= 2 && month <= 4) return 'spring';
    if (month >= 5 && month <= 7) return 'summer';
    return 'autumn';
  }

  // Обработчик сохранения профиля
  const handleSaveProfile = () => {
    if (showSaveDialog) {
      const savedResult = saveProfile(saveProfileName || null);
      if (savedResult) {
        alert(`Профиль "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveProfileName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateWaterIntake();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Droplet className="w-6 h-6 text-blue-500" />
          Калькулятор суточной нормы воды
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Личные параметры */}
        <PersonalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Активность и окружение */}
        <ActivityAndEnvironmentSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          activityLevels={activityLevels}
          climateTypes={climateTypes}
          seasons={seasons}
        />

        {/* Состояние здоровья */}
        <HealthConditionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          healthConditionsList={healthConditionsList}
        />

        {/* Настройки потребления воды */}
        <IntakePreferencesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения профиля */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название профиля"
                value={saveProfileName}
                onChange={(e) => setSaveProfileName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveProfile}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Droplet className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать норму воды'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать результаты (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveProfile}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить профиль
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;